/*
 * Copyright (c) 2024, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import isEqual from 'lodash/isEqual'
import {useEffect} from 'react'
import {useLocation, useHistory} from 'react-router-dom'
import {
    useUsid,
    useShopperContext,
    useShopperContextsMutation,
} from '@salesforce/commerce-sdk-react'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'

// Hooks
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

// Constants

import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useLocaleCountry} from './use-locale-custom'

/*
 * This hook will set the shopper context when search params pertinant
 * to shopper context are present.
 */
export const useUpdateShopperContext = () => {
    const {site, locale} = useMultiSite()
    const {usid} = useUsid()
    const {data: customer} = useCurrentCustomer()
    //const queryClient = useQueryClient()
    const createShopperContext = useShopperContextsMutation('createShopperContext')
    const updateShopperContext = useShopperContextsMutation('updateShopperContext')
    const currentCountry = useLocaleCountry()

    const history = useHistory()
    const location = useLocation()

    const {data: shopperContext, isLoading: isLoadingShopperContext} = useShopperContext(
        {parameters: {usid, siteId: site.id}},
        {enabled: !isServer},
    )

    /* 
    const refetchDataOnClient = () => {
        queryClient.invalidateQueries({queryKey: ['/shopper-context/']})
    } 
    */

    const handleShopperContextUpdate = async (shopperContext, newShopperContext) => {
        const payload = {
            parameters: {usid, siteId: site.id},
            body: newShopperContext,
        }
        if (!shopperContext) {
            await createShopperContext.mutateAsync(payload)
        } else {
            await updateShopperContext.mutateAsync(payload)
        }
    }

    const createSourceCodeShopperContextObject = (cluid) => {
        let sourceCodeObject = {}
        if (cluid)
            sourceCodeObject = {
                sourceCode: cluid,
                customQualifiers: {
                    isCorporate: true,
                },
            }
        else {
            sourceCodeObject = {
                sourceCode: '',
            }
        }
        return sourceCodeObject
    }

    const queryParams = new URLSearchParams(location.search)
    const getShopperContextSearchParams = () => {
        let shopperContextFromSearchParams = {}
        const cluid = queryParams.get('cluid')
        if (cluid) shopperContextFromSearchParams = createSourceCodeShopperContextObject(cluid)
        return shopperContextFromSearchParams
    }

    const shouldUpdateShopperContext = (shopperContext, newShopperContext) => {
        const shopperContextToCompare = {...shopperContext}
        if (shopperContextToCompare?.customQualifiers?.isCorporate)
            shopperContextToCompare.customQualifiers.isCorporate = Boolean(
                shopperContextToCompare.customQualifiers.isCorporate,
            )

        return !isEqual(shopperContextToCompare, newShopperContext)
    }

    // Handle updating the shopper context based on the locale
    // This will assign the customer to a customer group based on the locale
    useEffect(() => {
        if (isLoadingShopperContext) return

        let shopperContextFromSearchParams = getShopperContextSearchParams()

        if (shopperContextFromSearchParams?.sourceCode) {
            localStorage.setItem('cluid', shopperContextFromSearchParams.sourceCode)
            queryParams.delete('cluid')
            history.replace({search: queryParams.toString()})
        } else {
            const cluid = localStorage.getItem('cluid')
            shopperContextFromSearchParams = createSourceCodeShopperContextObject(cluid)
        }

        if (locale.id) {
            const {customQualifiers, sourceCode} = shopperContextFromSearchParams
            const newShopperContext = {
                customQualifiers: {
                    countryCode: currentCountry,
                    isCorporate: typeof sourceCode === 'string' && sourceCode !== '',
                    ...customQualifiers,
                },
                sourceCode,
            }

            if (shouldUpdateShopperContext(shopperContext, newShopperContext))
                handleShopperContextUpdate(shopperContext, newShopperContext)
        }
    }, [locale.id, customer.isRegistered, isLoadingShopperContext])
}
